/**
 * When the language model can't take the live translator's sentences (SEO_BRIEF_10 section
 * 2.1, the founder's rulings 1 and 3 of 2026-10-26): every model spent for the day, the live
 * translator's half of it used up, or a per-visitor cap that env switched on. The page says
 * Brief 09's sentence and nothing about any limit:
 *
 *   "Translation and summaries aren't available right now. They'll be back by 4:00 PM.
 *    Transcription still works."
 *
 * and, when the translation is into English, offers Hold to talk, which still translates into
 * English (section 2.4). The same sentence for every scope (lib/gemini.ts `limitMessage`).
 */

import type { GeminiLimit } from '../gemini.ts';
import { limitMessage } from '../gemini.ts';
import type { PageLang } from '../i18n.ts';
import { untilPacificMidnight } from '../states.ts';

export interface LimitNotice {
  /** Brief 09's sentence, with the time it comes back in the visitor's own clock. */
  readonly message: string;
  /** Offer Hold to talk beside it: the target is English and this browser can record. */
  readonly offerHold: boolean;
}

/**
 * What the page shows for `limit`, for a sentence into `target`. `canRecord`: the browser can
 * record a clip; `holding`: Hold to talk is already what the buttons do, so there is nothing to
 * offer.
 */
export function limitNotice(
  limit: GeminiLimit,
  target: string,
  canRecord: boolean,
  holding: boolean,
  lang?: PageLang,
): LimitNotice {
  return {
    message: limitMessage(limit, lang),
    offerHold: target === 'en' && canRecord && !holding,
  };
}

/** Whether `limit` still holds at `nowMs`: until the time it comes back. */
export function stillLimited(limit: GeminiLimit | null, nowMs: number): boolean {
  return limit !== null && limit.resetsAt * 1000 > nowMs;
}

/**
 * The limit for a Hold to talk clip the language model had no room to translate (the
 * transcript's `translation_unavailable`), which names no time: everyone's day, back at the
 * next midnight Pacific, as every limit is.
 */
export function spentForToday(nowMs: number = Date.now()): GeminiLimit {
  const left = untilPacificMidnight(nowMs) ?? 3_600;
  return {
    feature: 'live',
    scope: 'global',
    resetsAt: Math.floor(nowMs / 1000) + Math.max(60, Math.round(left)),
    cap: 0,
    fallback: null,
    holdToTalk: false,
  };
}
